import { sanitizeRegionalHighlightAssignableListingPayload } from '@/helpers/sanitizeRegionalHighlightAssignableListingCard';

import {
  RegionalHighlightAssignableListingsSource,
  RegionalHighlightListingMatchAction,
  RegionalHighlightListingMatchReason,
  type RegionalHighlightAssignableListing,
  type RegionalHighlightAssignableListingsMode,
  type RegionalHighlightAssignableListingsResponse,
  type RegionalHighlightListingMatch,
  type RegionalHighlightResolvedListing,
} from '@/types/regionalHighlight';

type RecordValue = Record<string, unknown>;

export const REGIONAL_HIGHLIGHT_ASSIGNABLE_LISTINGS_PAGE_SIZE = 8;

const asRecord = (value: unknown): RecordValue | undefined =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as RecordValue)
    : undefined;

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const asListingId = (value: unknown): string | undefined => {
  const stringValue = asString(value)?.trim();
  if (stringValue) {
    return stringValue;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return undefined;
};

const asFiniteNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const asPositiveInt = (value: unknown, fallback: number): number => {
  const parsed = asFiniteNumber(value);
  if (parsed === undefined) {
    return fallback;
  }
  const integer = Math.trunc(parsed);
  return integer >= 0 ? integer : fallback;
};

const normalizeSource = (value: unknown): RegionalHighlightAssignableListingsSource =>
  value === RegionalHighlightAssignableListingsSource.REFERENCE
    ? RegionalHighlightAssignableListingsSource.REFERENCE
    : RegionalHighlightAssignableListingsSource.ACTIVE;

const normalizeMode = (value: unknown): RegionalHighlightAssignableListingsMode =>
  value === 'replace' ? 'replace' : 'assign';

const normalizeListingMatch = (value: unknown): RegionalHighlightListingMatch | undefined => {
  const match = asRecord(value);
  const reason = match?.reason;
  const action = match?.action;
  if (
    !match ||
    typeof match.assignable !== 'boolean' ||
    !Object.values(RegionalHighlightListingMatchReason).includes(
      reason as RegionalHighlightListingMatchReason,
    )
  ) {
    return undefined;
  }

  return {
    assignable: match.assignable,
    reason: reason as RegionalHighlightListingMatchReason,
    ...(Object.values(RegionalHighlightListingMatchAction).includes(
      action as RegionalHighlightListingMatchAction,
    )
      ? { action: action as RegionalHighlightListingMatchAction }
      : {}),
  };
};

const normalizeIneligibleReason = (
  value: unknown,
): RegionalHighlightResolvedListing['ineligibleReason'] =>
  value === 'LISTING_NOT_FOUND' ||
  value === 'LISTING_NOT_FOR_SALE' ||
  value === 'LISTING_DISTRICT_UNRESOLVED'
    ? value
    : undefined;

const normalizeOfferType = (value: unknown): RegionalHighlightResolvedListing['offerType'] => {
  if (value === 'RENT') {
    return 'RENT';
  }
  if (value === 'BUY') {
    return 'BUY';
  }
  return undefined;
};

const normalizeDistrictNames = (value: unknown): Record<string, string> | undefined => {
  const names = asRecord(value);
  if (!names) return undefined;

  return Object.entries(names).reduce<Record<string, string>>((result, [locale, label]) => {
    if (typeof label === 'string') result[locale] = label;
    return result;
  }, {});
};

const normalizeResolvedListing = (value: unknown): RegionalHighlightResolvedListing | undefined => {
  const listing = asRecord(value);
  if (!listing) {
    return undefined;
  }

  const listingId = asString(listing.listingId);
  if (!listingId) {
    return undefined;
  }

  return {
    listingId,
    resolved: listing.resolved === true,
    offerType: normalizeOfferType(listing.offerType),
    districtId: asString(listing.districtId),
    districtName: normalizeDistrictNames(listing.districtName),
    eligible: listing.eligible === true,
    ineligibleReason: normalizeIneligibleReason(listing.ineligibleReason),
  };
};

const normalizeAssignableListing = (
  value: unknown,
): RegionalHighlightAssignableListing | undefined => {
  const raw = asRecord(value);
  const listingId = asListingId(raw?.listingId);
  if (!raw || !listingId) {
    return undefined;
  }

  const listing = sanitizeRegionalHighlightAssignableListingPayload(raw.listing);

  return {
    listingId,
    listingMatch: normalizeListingMatch(raw.listingMatch),
    ...(listing ? { listing } : {}),
    resolvedListing: normalizeResolvedListing(raw.resolvedListing),
  };
};

export const normalizeRegionalHighlightAssignableListingsResponse = (
  value: unknown,
): RegionalHighlightAssignableListingsResponse => {
  const raw = asRecord(value);
  const rawListings = raw?.listings;

  return {
    slotId: asString(raw?.slotId) ?? '',
    districtId: asString(raw?.districtId),
    districtLabel: asString(raw?.districtLabel),
    source: normalizeSource(raw?.source),
    mode: normalizeMode(raw?.mode),
    page: asPositiveInt(raw?.page, 1) || 1,
    pageSize:
      asPositiveInt(raw?.pageSize, REGIONAL_HIGHLIGHT_ASSIGNABLE_LISTINGS_PAGE_SIZE) ||
      REGIONAL_HIGHLIGHT_ASSIGNABLE_LISTINGS_PAGE_SIZE,
    totalCount: asPositiveInt(raw?.totalCount, 0),
    totalPages: asPositiveInt(raw?.totalPages, 0),
    listings: Array.isArray(rawListings)
      ? rawListings
          .map(normalizeAssignableListing)
          .filter((listing): listing is RegionalHighlightAssignableListing => Boolean(listing))
      : [],
  };
};
